import * as React from 'react';
import { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid';
import { Typography, Button, TextField, IconButton, InputAdornment, Box, InputLabel, Stack } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { forgotApi } from './forgortPassword.service'
import { encryptData } from '../../../helper/utils';
import Loader from '../../components/loader';
import NotificationSnackbar from '../../components/notificationSnackbar';
import {
  authShellSx,
  authMarketingPanelSx,
  authMarketingInnerSx,
  authBrandSx,
  authHeadlineSx,
  authSubheadlineSx,
  authCapabilityPillSx,
  authFormPanelSx,
  authCardSx,
  authTitleSx,
  authSubtitleSx,
  authLabelSx,
  authFieldSx,
  authPrimaryButtonSx,
  authSecondaryButtonSx,
  requiredAsteriskSx,
} from '../../styles/authStyles';

function ForgotPassword() {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forgottype = searchParams.get("type");
  const token = searchParams.get("token");
  const [showFields, setShowFields] = useState(false);
  const [newPassword, setnewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState("");

  const ALERT_DURATION = 2000;
  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  }

  useEffect(() => {
    if (token) {
      setShowFields(true);
    }
  }, [token]);

  const forgotPasswordSchema = z
    .object({
      email: z.string().optional(),
      password: z.string().optional(),
      confirmPassword: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const isReset = forgottype === "reset-password" || !!token;
      if (!isReset) {
        if (!data.email) {
          ctx.addIssue({
            path: ["email"],
            code: "custom",
            message: "Email is required",
          });
        } else {
          // ✅ Custom email format check (NOT using .email())
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(data.email)) {
            ctx.addIssue({
              path: ["email"],
              code: "custom",
              message: "Invalid email format",
            });
          }
        }
      }
      if (isReset) {
        if (!data.password) {
          ctx.addIssue({
            path: ["password"],
            code: "custom",
            message: "New password is required",
          });
        }

        if (!data.confirmPassword) {
          ctx.addIssue({
            path: ["confirmPassword"],
            code: "custom",
            message: "Confirm password is required",
          });
        }

        if (
          data.password &&
          data.confirmPassword &&
          data.password !== data.confirmPassword
        ) {
          ctx.addIssue({
            path: ["confirmPassword"],
            code: "custom",
            message: "Passwords must match",
          });
        }
      }
    });


  const toggleNewPasswordVisibility = () => {
    setnewPassword(!newPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPassword(!confirmPassword);
  };

  const { control, handleSubmit, formState: { errors } } = useForm({ resolver: zodResolver(forgotPasswordSchema), });

  const onSubmit = async (data) => {

    let encrpytedPassword = { ...data }

    if (encrpytedPassword?.password && encrpytedPassword?.confirmPassword) {
      var encryptedNewPassword = encryptData(encrpytedPassword?.password)
      var encryptedConfirmPassword = encryptData(encrpytedPassword?.confirmPassword)
      encrpytedPassword.password = encryptedNewPassword;
      encrpytedPassword.confirmPassword = encryptedConfirmPassword;
    }

    encrpytedPassword.email = encrpytedPassword.email ? encrpytedPassword.email.toLocaleLowerCase() : ''
    setLoading(true)
    const res = await forgotApi(forgottype, encrpytedPassword, token);
    if (res.statusCode === 200 || res.statusCode === 201) {
      setResponseMsg(res.message);
      setAlert('true');
      setLoading(false)
      setOpenAlert(true)
      setTimeout(() => {
        alertClose();
        navigate('/auth/login')
      }, ALERT_DURATION);
    } else if (res.statusCode === 400 || res.statusCode === 404 || res.statusCode === 500 || res.statusCode === 429) {
      setResponseMsg(res.message);
      setAlert('false');
      setLoading(false)
      setOpenAlert(true)
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
    } else {
      setResponseMsg(res.message);
      setAlert('true');
      setLoading(false)
      setOpenAlert(true)
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
    }
  };

  return (
    <>
      <Grid container sx={authShellSx}>
        <Grid size={{ sm: 12, xs: 12, md: 5, lg: 5, xl: 5 }} sx={{ order: { xs: 2, md: 1 }, ...authMarketingPanelSx }}>
          <Box sx={authMarketingInnerSx}>
            <Box pb={5}>
              <Typography sx={authBrandSx}>eKutir</Typography>
              <Typography sx={authHeadlineSx}>Global AgTech Platform</Typography>
              <Typography sx={authSubheadlineSx}>Powering Agricultural Innovation</Typography>
            </Box>
            <Stack spacing={2.25} alignItems="center">
              {['ALLIN FIX', 'ALLIN FIT', 'ALLIN FLEX', 'ALLIN IMPACT', 'ALLIN TRUST'].map((item) => (
                <Typography key={item} sx={authCapabilityPillSx}>{item}</Typography>
              ))}
            </Stack>
          </Box>
        </Grid>

        <Grid size={{ sm: 12, xs: 12, md: 7, lg: 7, xl: 7 }} sx={{ order: { xs: 1, md: 2 }, ...authFormPanelSx }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%', maxWidth: 540 }}>
            <Box sx={authCardSx}>

            <Box pb={4} sx={{ textAlign: "center" }}>
              <Typography sx={authTitleSx}>Forgot Password</Typography>
              <Typography sx={authSubtitleSx}>
                {showFields ? 'Create a new password for your account' : 'Enter your email to receive reset instructions'}
              </Typography>
            </Box>

            <Grid container rowSpacing={3} justifyContent={'center'}>
              {forgottype !== 'reset-password' && !showFields && (
                <Grid size={12}>
                  <InputLabel shrink htmlFor="email" sx={authLabelSx}>Email Address <Box component="span" sx={requiredAsteriskSx}>*</Box></InputLabel>
                  <Controller name="email" control={control} render={({ field, fieldState: { error } }) => (
                    <TextField {...field} fullWidth placeholder="your@email.com" type="text" variant="outlined" error={!!errors.email} helperText={errors.email ? errors.email.message : ''} sx={authFieldSx} />
                  )} />
                </Grid>
              )}
              {showFields ?
                <>
                  <Grid size={12}>
                    <InputLabel shrink htmlFor="email" sx={authLabelSx}>New Password <Box component="span" sx={requiredAsteriskSx}>*</Box></InputLabel>
                    <Controller name="password" control={control} render={({ field, fieldState: { error } }) => (
                      <TextField {...field} fullWidth placeholder="Enter new password" type={newPassword ? 'text' : 'password'} variant="outlined"
                        error={!!errors.password} helperText={errors.password ? errors.password.message : ''}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => toggleNewPasswordVisibility(!newPassword)} sx={{ color: '#6C6A86' }}>
                                {newPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={authFieldSx}
                      />)}
                    />
                  </Grid>

                  <Grid size={12}>
                    <InputLabel shrink htmlFor="email" sx={authLabelSx}>Confirm Password <Box component="span" sx={requiredAsteriskSx}>*</Box></InputLabel>
                    <Controller name="confirmPassword" control={control} render={({ field, fieldState: { error } }) => (
                      <TextField {...field} fullWidth placeholder="Confirm new password" type={confirmPassword ? 'text' : 'password'} variant="outlined"
                        error={!!errors.confirmPassword} helperText={errors.confirmPassword ? errors.confirmPassword.message : ''}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => toggleConfirmPasswordVisibility(!confirmPassword)} sx={{ color: '#6C6A86' }}>
                                {confirmPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={authFieldSx}
                      />
                    )} />
                  </Grid>
                </>
                : ''}
            </Grid>

            <Grid container spacing={2} mt={4} justifyContent={'center'}>
              <Grid size={{ sm: 4, xs: 12, md: 4, lg: 4, xl: 4 }}>
                <Button variant="outlined" fullWidth sx={authSecondaryButtonSx} onClick={() => navigate('/auth/login')}>Cancel</Button>
              </Grid>
              <Grid size={{ sm: 8, xs: 12, md: 8, lg: 8, xl: 8 }} textAlign={'end'}>
                <Button variant="contained" type="submit" fullWidth sx={authPrimaryButtonSx}>{forgottype === 'reset-password' ? 'Submit' : 'Send Email'}</Button>
              </Grid>
            </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {loading && <Loader />}

      <NotificationSnackbar
        open={openAlert && !!alert}
        onClose={alertClose}
        message={responseMsg}
        severity={alert === 'true' ? 'success' : 'error'}
        messageTestId={alert === 'true' ? 'alert-success' : 'alert-error'}
      />
    </>
  )
}

export default ForgotPassword
